"use client"

import { MapPin, ChefHat, ShoppingBag, Search, ShieldCheck, Store, BookOpen, ScanLine, UtensilsCrossed } from "lucide-react"
import type { ReactNode } from "react"

interface QuickAction {
  icon: ReactNode
  label: string
  query: string
}

interface QuickActionsProps {
  actions: QuickAction[]
  onAction: (query: string) => void
  disabled?: boolean
}

export function QuickActions({ actions, onAction, disabled }: QuickActionsProps) {
  if (!actions.length) return null

  return (
    <div className="flex flex-wrap gap-2 mt-3 mb-1">
      {actions.map((action, i) => (
        <button
          key={i}
          onClick={() => onAction(action.query)}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-full border border-emerald-200 transition-all hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {action.icon}
          {action.label}
        </button>
      ))}
    </div>
  )
}

const iconClass = "w-3.5 h-3.5"

const ACTION_PATTERNS: [RegExp, QuickAction[]][] = [
  [/store|shop|grocery|restaurant|fukuoka|tokyo|osaka|nagoya/i, [
    { icon: <MapPin className={iconClass} />, label: "More halal spots", query: "Show me more halal stores and restaurants nearby" },
    { icon: <ChefHat className={iconClass} />, label: "Cook at home?", query: "Suggest a halal recipe I can cook at home using ingredients from these stores" },
    { icon: <ShoppingBag className={iconClass} />, label: "What can I buy?", query: "What halal products are available at these stores?" },
  ]],
  [/recipe|cook|make|biryani|teriyaki|curry/i, [
    { icon: <ShoppingBag className={iconClass} />, label: "Shopping list", query: "Create a shopping list for this recipe" },
    { icon: <MapPin className={iconClass} />, label: "Buy ingredients", query: "Where can I buy the ingredients for this recipe?" },
    { icon: <BookOpen className={iconClass} />, label: "Another recipe", query: "Suggest another halal recipe I can make in Japan" },
  ]],
  [/halal|haram|doubtful|mashbooh|gelatin|pork|alcohol/i, [
    { icon: <Search className={iconClass} />, label: "Check another", query: "I want to check if another ingredient is halal" },
    { icon: <ShoppingBag className={iconClass} />, label: "Halal alternative", query: "What halal alternatives are available in Japan?" },
    { icon: <MapPin className={iconClass} />, label: "Halal stores", query: "Find halal grocery stores near me" },
  ]],
  [/product|snack|ramen|pocky|candy|chocolate|gummy|yogurt|yoghurt/i, [
    { icon: <ShieldCheck className={iconClass} />, label: "Check another", query: "I want to check if another product is halal" },
    { icon: <Store className={iconClass} />, label: "Konbini options", query: "What other halal snacks can I find at convenience stores in Japan?" },
    { icon: <ShoppingBag className={iconClass} />, label: "Verified halal", query: "Show me all verified halal products available in Japan" },
  ]],
  [/label|ingredient|原材料/i, [
    { icon: <ScanLine className={iconClass} />, label: "Another label", query: "I have another food label to check for halal compliance" },
    { icon: <ShoppingBag className={iconClass} />, label: "Halal version", query: "Help me find a halal-certified version of this product" },
    { icon: <MapPin className={iconClass} />, label: "Halal stores", query: "Find halal grocery stores near me" },
  ]],
]

export function extractQuickActions(agentResponse: string): QuickAction[] {
  for (const [pattern, actions] of ACTION_PATTERNS) {
    if (pattern.test(agentResponse)) return actions
  }
  return [
    { icon: <UtensilsCrossed className={iconClass} />, label: "Where to eat", query: "Where can I eat halal food nearby?" },
    { icon: <MapPin className={iconClass} />, label: "Halal stores", query: "Find halal grocery stores near me" },
    { icon: <ShieldCheck className={iconClass} />, label: "Check ingredient", query: "I want to check if a food ingredient is halal" },
  ]
}
